/**
 * Dashboard Page
 */
import { useState, useEffect, useCallback } from 'react';
import { api, type Receipt, type Income, type FinancialSummary } from '../lib/api';
import { useOrganizationChange } from '../hooks/useOrganizationChange';

interface Stats {
  totalReceipts: number;
  totalAmount: number;
  thisMonth: number;
  recentReceipts: Receipt[];
  recentIncomes: Income[];
  financialSummary: FinancialSummary | null;
}

export function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const [receiptsResult, incomesResult, financialResult] = await Promise.all([
        api.getReceipts({ limit: 100 }),
        api.getIncomes({ limit: 100 }),
        api.getFinancialSummary({}),
      ]);

      const receipts = receiptsResult.success && receiptsResult.data ? receiptsResult.data.receipts : [];
      const incomes = incomesResult.success && incomesResult.data ? incomesResult.data.income : [];
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const totalAmount = receipts.reduce((sum, r) => sum + r.total, 0);
      const thisMonth = receipts
        .filter((r) => new Date(r.date) >= startOfMonth)
        .reduce((sum, r) => sum + r.total, 0);

      setStats({
        totalReceipts: receiptsResult.success && receiptsResult.data ? receiptsResult.data.pagination.total : 0,
        totalAmount,
        thisMonth,
        recentReceipts: receipts.slice(0, 5),
        recentIncomes: incomes.slice(0, 5),
        financialSummary: financialResult.success && financialResult.data ? financialResult.data : null,
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh when organization changes
  useOrganizationChange(loadStats);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const formatCurrency = (amount: number) => {
    return `HKD ${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-6 sm:mb-8"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg p-4 sm:p-6 h-24 sm:h-32"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 sm:mb-8">Dashboard</h1>

      {/* Financial Summary */}
      {stats?.financialSummary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-l-4 border-green-500">
            <div className="text-sm font-medium text-gray-500 mb-1">Total Income</div>
            <div className="text-2xl sm:text-3xl font-bold text-green-600 truncate">
              {formatCurrency(stats.financialSummary.income.total)}
            </div>
            <div className="text-xs text-gray-400 mt-1">{stats.financialSummary.income.count} entries</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-l-4 border-red-500">
            <div className="text-sm font-medium text-gray-500 mb-1">Total Expenses</div>
            <div className="text-2xl sm:text-3xl font-bold text-red-600 truncate">
              {formatCurrency(stats.financialSummary.expenses.total)}
            </div>
            <div className="text-xs text-gray-400 mt-1">{stats.financialSummary.expenses.count} receipts</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-l-4 border-blue-500">
            <div className="text-sm font-medium text-gray-500 mb-1">Net Balance</div>
            <div className={`text-2xl sm:text-3xl font-bold truncate ${
              stats.financialSummary.net >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {stats.financialSummary.net >= 0 ? '+' : ''}{formatCurrency(stats.financialSummary.net)}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {stats.financialSummary.net >= 0 ? 'Surplus' : 'Deficit'}
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="text-sm font-medium text-gray-500 mb-1">
            Total Receipts
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-gray-900">
            {stats?.totalReceipts || 0}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="text-sm font-medium text-gray-500 mb-1">
            Total Amount
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">
            {formatCurrency(stats?.totalAmount || 0)}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6 sm:col-span-2 lg:col-span-1">
          <div className="text-sm font-medium text-gray-500 mb-1">
            This Month (Expenses)
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-blue-600 truncate">
            {formatCurrency(stats?.thisMonth || 0)}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Receipts */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Recent Expenses</h2>
          </div>
          {stats?.recentReceipts && stats.recentReceipts.length > 0 ? (
            <div className="divide-y">
              {stats.recentReceipts.map((receipt) => (
                <div
                  key={receipt.id}
                  className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="min-w-0 flex-1 mr-4">
                    <div className="font-medium text-gray-900 truncate">
                      {receipt.vendor || 'Unknown Vendor'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDate(receipt.date)}
                    </div>
                  </div>
                  <div className="font-semibold text-red-600 whitespace-nowrap">
                    -{receipt.currency} {receipt.total.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 sm:px-6 py-6 sm:py-8 text-center text-gray-500">
              No receipts yet
            </div>
          )}
        </div>

        {/* Recent Income */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Recent Income</h2>
          </div>
          {stats?.recentIncomes && stats.recentIncomes.length > 0 ? (
            <div className="divide-y">
              {stats.recentIncomes.map((income) => (
                <div
                  key={income.id}
                  className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="min-w-0 flex-1 mr-4">
                    <div className="font-medium text-gray-900 truncate">
                      {income.description}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDate(income.date)}
                    </div>
                  </div>
                  <div className="font-semibold text-green-600 whitespace-nowrap">
                    +{income.currency} {income.amount.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 sm:px-6 py-6 sm:py-8 text-center text-gray-500">
              No income entries yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
